import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { InjectedRouter } from "react-router";
import { connect } from "react-redux";
import type { Location } from "history";
import type { Dispatch } from "redux";
import { makeStyles } from "@material-ui/core/styles";
import { ContentLoading } from "components/content-loading";
import siteConfig from "site-config";
import PageNotFound from "containers/page-not-found";
import { trackConnectionError } from "actions/marketplace";
import type { IStore } from "reducers";
import { MARKETPLACE } from "constants/navigation";

// URL of the Marketplace for correct internal redirects
interface IDispatchProps {
    onError: typeof trackConnectionError;
}

interface IStateProps {
    location: Location;
}

interface IProps {
    router: InjectedRouter;
}

export const MarketplaceContainer: FC<IStateProps & IDispatchProps & IProps> = ({ onError, location, router }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [error, setError] = useState<boolean>(null);
    const [internalURL, setInternalURL] = useState<string>(getLocalUrl(location.pathname));
    const ref = useRef<HTMLIFrameElement>(null);

    const handleLoad = useCallback(() => setIsLoaded(true), []);
    const handleError = useCallback(() => {
        setError(true);
        onError();
    }, [onError]);

    const handlePostMessage = useCallback((data: any) => {
        setTimeout(() => ref.current?.contentWindow?.postMessage(data, "*"), 100);
    }, []);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.data?.redirect === "/cart" && event.data?.payload) {
                window.sessionStorage.setItem("lastActivePath", new URL(window.location.href).pathname); // redirect to Marketplace on WO close
                router.push(`/brand/${event.data?.payload}/cart`);
            } else if (event.data?.redirect === "/nuorder" && event.data?.payload) {
                router.push(`${event.data?.payload}`);
            } else if (event.data?.redirect && event.data?.redirect !== getLocalUrl()) {
                // WARNING: hacky solution to update the browser URL without causing a page reload which is not supported in React Router 3
                // Required to avoid UI glitches while the iframe includes the Nav bar that should always be visible
                setInternalURL(event.data?.redirect);
                const newUrl = new URL(window.location.href);
                newUrl.pathname = MARKETPLACE + event.data?.redirect;
                window.history.pushState({}, "", newUrl.toString());
            }
        };
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [router]);

    useEffect(() => {
        // WARNING: processing of browser Back/Forward buttons via direct way to avoid React Router page changes
        const handleRedirect = () => {
            if (window.location.href?.includes(MARKETPLACE) && getLocalUrl() !== internalURL) {
                handlePostMessage({ redirect: getLocalUrl() });
            }
        };
        window.addEventListener("popstate", handleRedirect);
        return () => window.removeEventListener("popstate", handleRedirect);
    }, [handlePostMessage, internalURL]);

    useEffect(() => {
        if (location.pathname.includes(MARKETPLACE) && getLocalUrl(location.pathname) !== internalURL) {
            const localUrl = getLocalUrl(location.pathname);
            setInternalURL(localUrl);
            handlePostMessage({ redirect: localUrl });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, handlePostMessage]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const url = useMemo(() => `${siteConfig.MARKETPLACE_URL}${getLocalUrl(location.pathname)}`, []); // caching the url once
    const classes = useStyles();

    return (
        <>
            {!isLoaded && !error && <ContentLoading />}
            {!isLoaded && error && <PageNotFound />}
            <iframe className={classes.root} onLoad={handleLoad} onError={handleError} src={url} ref={ref} />
        </>
    );
};

const useStyles = makeStyles({
    root: {
        width: "100%",
        height: "100%",
        display: "block",
    },
});

const getLocalUrl = (url?: string) => (url || new URL(window.location.href).pathname)?.replace(MARKETPLACE, "") || "/";

const mapDispatchToProps = (dispatch: Dispatch<{}>): IDispatchProps => ({
    onError: () => dispatch(trackConnectionError()),
});

const mapStateToProps = (state: IStore): IStateProps => ({
    location: state.routing.locationBeforeTransitions,
});

export default connect<IStateProps, IDispatchProps, IProps>(mapStateToProps, mapDispatchToProps)(MarketplaceContainer);
